import { all, call, put, select, takeLatest } from 'redux-saga/effects';

import {
  AgentApiError,
  createAgentOrderTourist,
  deleteAgentOrderTourist,
  getAgentApiErrorMessage,
  getAgentOrder,
  getAgentOrders,
  serializeAgentMeta,
  serializeAgentOrder,
  updateAgentOrderTourist,
} from '../../modules/b2b/api';
import { AGENT_AUTH_ROUTE } from '../../modules/b2b/auth/config';
import { withAppBasePath } from '../../modules/appMode';
import {
  createB2BOrderTouristAction,
  deleteB2BOrderTouristAction,
  fetchB2BOrdersAction,
  selectB2BOrderAction,
  updateB2BOrderTouristAction,
} from '../actions/b2bOrders';
import {
  appendOrders,
  setDeletingTouristId,
  setActiveOrder,
  setActiveOrderId,
  setError,
  setIsLoading,
  setIsLoadingActiveOrder,
  setIsSavingTourist,
  setMeta,
  setOrders,
  setTouristMutationError,
} from '../reducers/b2bOrders';
import { activeB2BOrderIdSelector } from '../selectors/b2bOrders';
import { safeApply } from './errors';

const handleUnauthorized = (error: unknown) => {
  if (error instanceof AgentApiError && error.status === 401) {
    window.location.assign(withAppBasePath(AGENT_AUTH_ROUTE));
    return true;
  }

  return false;
};

function* fetchB2BOrders({ payload }: ReturnType<typeof fetchB2BOrdersAction>) {
  const page = payload?.page || 1;

  yield put(setIsLoading(true));
  yield put(setError(null));

  if (page === 1) {
    yield put(setOrders([]));
  }

  try {
    const response: Awaited<ReturnType<typeof getAgentOrders>> = yield call(
      getAgentOrders,
      payload
    );

    const orders = response.orders.map(serializeAgentOrder);

    yield put(page > 1 ? appendOrders(orders) : setOrders(orders));
    yield put(setMeta(serializeAgentMeta(response.meta)));
  } catch (error) {
    if (!handleUnauthorized(error)) {
      yield put(setError(getAgentApiErrorMessage(error)));
    }
  } finally {
    yield put(setIsLoading(false));
  }
}

function* selectB2BOrder({ payload }: ReturnType<typeof selectB2BOrderAction>) {
  yield put(setActiveOrderId(payload.id));
  yield put(setActiveOrder(null));
  yield put(setIsLoadingActiveOrder(true));
  yield put(setError(null));
  yield put(setTouristMutationError(null));

  try {
    const response: Awaited<ReturnType<typeof getAgentOrder>> = yield call(
      getAgentOrder,
      payload.id
    );

    yield put(setActiveOrder(serializeAgentOrder(response.order)));
  } catch (error) {
    if (!handleUnauthorized(error)) {
      yield put(setError(getAgentApiErrorMessage(error)));
    }
  } finally {
    yield put(setIsLoadingActiveOrder(false));
  }
}

function* reloadActiveOrder(orderId: string) {
  const response: Awaited<ReturnType<typeof getAgentOrder>> = yield call(
    getAgentOrder,
    orderId
  );
  const activeOrderId: string | null = yield select(activeB2BOrderIdSelector);

  if (activeOrderId === orderId) {
    yield put(setActiveOrder(serializeAgentOrder(response.order)));
  }
}

function* createB2BOrderTourist({
  payload,
}: ReturnType<typeof createB2BOrderTouristAction>) {
  yield put(setIsSavingTourist(true));
  yield put(setTouristMutationError(null));

  try {
    yield call(createAgentOrderTourist, payload.orderId, payload.tourist);
    yield call(reloadActiveOrder, payload.orderId);
  } catch (error) {
    if (!handleUnauthorized(error)) {
      yield put(setTouristMutationError(getAgentApiErrorMessage(error)));
    }
  } finally {
    yield put(setIsSavingTourist(false));
  }
}

function* updateB2BOrderTourist({
  payload,
}: ReturnType<typeof updateB2BOrderTouristAction>) {
  yield put(setIsSavingTourist(true));
  yield put(setTouristMutationError(null));

  try {
    yield call(
      updateAgentOrderTourist,
      payload.orderId,
      payload.touristId,
      payload.tourist
    );
    yield call(reloadActiveOrder, payload.orderId);
  } catch (error) {
    if (!handleUnauthorized(error)) {
      yield put(setTouristMutationError(getAgentApiErrorMessage(error)));
    }
  } finally {
    yield put(setIsSavingTourist(false));
  }
}

function* deleteB2BOrderTourist({
  payload,
}: ReturnType<typeof deleteB2BOrderTouristAction>) {
  yield put(setDeletingTouristId(payload.touristId));
  yield put(setTouristMutationError(null));

  try {
    yield call(deleteAgentOrderTourist, payload.orderId, payload.touristId);
    yield call(reloadActiveOrder, payload.orderId);
  } catch (error) {
    if (!handleUnauthorized(error)) {
      yield put(setTouristMutationError(getAgentApiErrorMessage(error)));
    }
  } finally {
    yield put(setDeletingTouristId(null));
  }
}

export default function* b2bOrdersSaga() {
  yield all([
    takeLatest(fetchB2BOrdersAction, safeApply(fetchB2BOrders)),
    takeLatest(selectB2BOrderAction, safeApply(selectB2BOrder)),
    takeLatest(createB2BOrderTouristAction, safeApply(createB2BOrderTourist)),
    takeLatest(updateB2BOrderTouristAction, safeApply(updateB2BOrderTourist)),
    takeLatest(deleteB2BOrderTouristAction, safeApply(deleteB2BOrderTourist)),
  ]);
}
